import { useState, useEffect, useCallback, useRef } from 'react';
import { CommandDialog, CommandInput, CommandList, CommandEmpty } from '@/components/ui/command';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Check, X, Sparkles, AlertCircle, RefreshCw, Copy, Upload } from 'lucide-react';
import { AiTooltip } from '@/components/ai/AiTooltip';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { useEntitlement } from '@/hooks/useEntitlement';
import { FosButton, FosBadge } from '@/components/ui/flipperos';
import {
  ai,
  streamAI,
  isAiEstimate,
  ProxyHttpError,
  type NaturalLanguageQueryResult,
  type GenerateListingResult,
  type IdentifyItemResult,
  type AppraiseResult,
} from '@/services/ai';

const FREE_DAILY_LIMIT = 3;
const USAGE_KEY_PREFIX = 'forvisibility_ai_cmds_';

function todayKey() {
  return USAGE_KEY_PREFIX + new Date().toISOString().slice(0, 10);
}

function getUsageToday(): number {
  return parseInt(localStorage.getItem(todayKey()) ?? '0', 10);
}

function incrementUsage() {
  const current = getUsageToday();
  localStorage.setItem(todayKey(), String(current + 1));
}

export type CommandMode = 'nlq' | 'draft' | 'value' | 'identify' | 'appraise';

// eslint-disable-next-line react-refresh/only-export-components
export function detectCommand(input: string): { mode: CommandMode; text: string } {
  const trimmed = input.trim();
  if (/^\/identify\s+/i.test(trimmed))
    return { mode: 'identify', text: trimmed.replace(/^\/identify\s+/i, '') };
  if (/^\/draft\s+/i.test(trimmed))
    return { mode: 'draft', text: trimmed.replace(/^\/draft\s+/i, '') };
  if (/^\/appraise\s+/i.test(trimmed))
    return { mode: 'appraise', text: trimmed.replace(/^\/appraise\s+/i, '') };
  if (/^\/value\s+/i.test(trimmed))
    return { mode: 'value', text: trimmed.replace(/^\/value\s+/i, '') };
  return { mode: 'nlq', text: trimmed };
}

interface ErrorDisplay {
  message: string;
  retry?: () => void;
  upgrade?: boolean;
}

export function AIConsole() {
  const { user: _user } = useAuth();
  const { effectiveTier, isLoading: _entitlementLoading } = useEntitlement();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<NaturalLanguageQueryResult | null>(null);
  const [errDisplay, setErrDisplay] = useState<ErrorDisplay | null>(null);
  const [usageToday, setUsageToday] = useState(getUsageToday);
  const [commandMode, setCommandMode] = useState<CommandMode>('nlq');
  const [draftResult, setDraftResult] = useState<GenerateListingResult | null>(null);
  const [identifyResult, setIdentifyResult] = useState<IdentifyItemResult | null>(null);
  const [appraiseResult, setAppraiseResult] = useState<AppraiseResult | null>(null);
  // Streaming NLQ state
  const [streamingText, setStreamingText] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  // Uploaded image for /identify and /appraise commands
  const [uploadedImageBase64, setUploadedImageBase64] = useState<string | null>(null);
  const [uploadedImageMimeType, setUploadedImageMimeType] = useState<string>('image/jpeg');

  const isFree = effectiveTier === 'free';
  const remaining = isFree ? FREE_DAILY_LIMIT - usageToday : Infinity;
  const isLimitReached = isFree && usageToday >= FREE_DAILY_LIMIT;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleClose = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      abortRef.current?.abort();
      setOpen(false);
      setQuery('');
      setResult(null);
      setDraftResult(null);
      setIdentifyResult(null);
      setAppraiseResult(null);
      setErrDisplay(null);
      setStreamingText('');
      setUploadedImageBase64(null);
      setUploadedImageMimeType('image/jpeg');
    } else {
      setOpen(true);
    }
  }, []);

  function handleRetry() {
    setErrDisplay(null);
    handleSubmit();
  }

  function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedImageMimeType(file.type || 'image/jpeg');
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setUploadedImageBase64(dataUrl.split(',')[1] ?? '');
    };
    reader.readAsDataURL(file);
  }

  async function handleSubmit() {
    if (!query.trim() || isLoading) return;
    if (isLimitReached) {
      setErrDisplay({
        message: 'Daily limit reached. Upgrade to Pro for unlimited AI commands.',
        upgrade: true,
      });
      return;
    }

    // Cancel any previous streaming
    abortRef.current?.abort();

    setIsLoading(true);
    setResult(null);
    setDraftResult(null);
    setIdentifyResult(null);
    setAppraiseResult(null);
    setErrDisplay(null);
    setStreamingText('');
    incrementUsage();
    setUsageToday(getUsageToday());

    const { mode, text } = detectCommand(query);
    setCommandMode(mode);

    try {
      switch (mode) {
        case 'identify': {
          if (!uploadedImageBase64) {
            setErrDisplay({ message: 'Please upload an image first' });
            break;
          }
          const item = await ai.identifyItem({
            imageBase64: uploadedImageBase64,
            mimeType: uploadedImageMimeType,
          });
          setIdentifyResult(item);
          break;
        }
        case 'draft': {
          const listing = await ai.generateListing({
            name: text,
            category: 'General',
            condition: 'Good',
            description: text,
          });
          setDraftResult(listing);
          setResult({
            summary: listing.title,
            items: [
              {
                label: 'Description',
                value:
                  listing.description.slice(0, 140) + (listing.description.length > 140 ? '…' : ''),
              },
              { label: 'Condition note', value: listing.conditionStatement },
            ],
          });
          break;
        }
        case 'appraise': {
          if (!uploadedImageBase64) {
            setErrDisplay({ message: 'Please upload photos for appraisal' });
            break;
          }
          const appraisal = await ai.appraise({
            imageBase64s: [uploadedImageBase64],
            mimeTypes: [uploadedImageMimeType],
            condition: 'Good',
            era: 'Unknown',
            materials: text,
          });
          setAppraiseResult(appraisal);
          break;
        }
        case 'value': {
          const comps = await ai.searchComps({ query: text });
          const estimated = isAiEstimate(comps);
          setResult({
            summary: estimated
              ? `AI price estimate for: ${text} (no sold-comp data — verify before pricing)`
              : `Market comps for: ${text}`,
            items: [
              { label: 'Est. list price', value: `$${comps.suggestedListPrice.toFixed(2)}` },
              {
                label: estimated ? 'Est. median' : 'Median comp',
                value: `$${comps.stats.median.toFixed(2)}`,
              },
              {
                label: estimated ? 'Basis' : 'Comps found',
                value: estimated ? 'AI estimate' : String(comps.stats.count),
              },
            ],
          });
          break;
        }
        case 'nlq':
        default: {
          // Streaming NLQ: render tokens as they arrive, then commit a final result.
          const controller = new AbortController();
          abortRef.current = controller;
          let accumulated = '';
          await new Promise<void>((resolve) => {
            streamAI(
              'nlq',
              { query: text },
              (delta) => {
                accumulated += delta;
                setStreamingText(accumulated);
              },
              () => {
                setResult({ summary: accumulated });
                setStreamingText('');
                resolve();
              },
              (err) => {
                // Re-throw via outer catch by setting err display and resolving
                setErrDisplay({
                  message: err.message || 'Streaming failed',
                  retry: handleRetry,
                });
                resolve();
              },
              controller.signal,
            );
          });
          break;
        }
      }
    } catch (err) {
      if (err instanceof ProxyHttpError) {
        switch (err.code) {
          case 'RATE_LIMITED':
            setErrDisplay({
              message: 'Rate limited. Please wait a moment and try again.',
              retry: handleRetry,
            });
            break;
          case 'TRIAL_QUOTA_EXCEEDED':
            setErrDisplay({
              message: 'Trial AI quota reached. Upgrade to Pro for unlimited AI commands.',
              upgrade: true,
            });
            break;
          case 'UNAUTHORIZED':
            setErrDisplay({
              message: 'Session expired. Please sign in again.',
            });
            break;
          default:
            setErrDisplay({
              message: err.message || 'AI request failed',
              retry: handleRetry,
            });
        }
      } else {
        setErrDisplay({
          message: err instanceof Error ? err.message : 'Failed to process command',
          retry: handleRetry,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <CommandDialog open={open} onOpenChange={handleClose}>
      <div className="flex items-center justify-between px-4 pb-1 pt-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">AI Console</span>
        </div>
        {isFree ? (
          <FosBadge variant="secondary" className="text-xs">
            {Math.max(0, remaining)} / {FREE_DAILY_LIMIT} cmds left today
          </FosBadge>
        ) : (
          <FosBadge className="text-xs">Pro · Unlimited</FosBadge>
        )}
      </div>

      <CommandInput
        placeholder={
          commandMode === 'identify'
            ? 'Identify item...'
            : 'Ask anything… "Show items with margin under 30%"'
        }
        value={query}
        onValueChange={setQuery}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
          }
        }}
        disabled={isLoading}
      />

      <CommandList className="max-h-[400px]">
        {isLoading && !streamingText && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {commandMode === 'nlq' ? 'Thinking…' : 'Processing…'}
          </div>
        )}

        {streamingText && (
          <div className="space-y-2 p-3">
            <Card className="border-primary/20">
              <CardContent className="pt-4">
                <p className="whitespace-pre-wrap text-sm">
                  {streamingText}
                  {isLoading && <span className="ml-0.5 animate-pulse">▍</span>}
                </p>
              </CardContent>
            </Card>
            {isLoading && (
              <div className="flex justify-end">
                <FosButton
                  variant="outline"
                  size="sm"
                  onClick={() => abortRef.current?.abort()}
                  className="gap-1"
                >
                  <X className="h-3 w-3" />
                  Stop
                </FosButton>
              </div>
            )}
          </div>
        )}

        {/* Image upload area for /identify and /appraise */}
        {!isLoading &&
          !result &&
          !errDisplay &&
          (detectCommand(query).mode === 'identify' || detectCommand(query).mode === 'appraise') &&
          !uploadedImageBase64 && (
            <div className="border-t border-border/50 px-4 py-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <Upload className="h-4 w-4" />
                <span>
                  Upload a photo for{' '}
                  {detectCommand(query).mode === 'identify' ? 'identification' : 'appraisal'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </label>
            </div>
          )}

        {/* Uploaded image preview */}
        {uploadedImageBase64 && !isLoading && (
          <div className="flex items-center gap-2 border-t border-border/50 px-4 py-2">
            <img
              src={`data:${uploadedImageMimeType};base64,${uploadedImageBase64}`}
              alt="Uploaded"
              className="h-10 w-10 rounded-md object-cover"
            />
            <span className="text-xs text-muted-foreground">Photo attached</span>
            <button
              type="button"
              onClick={() => {
                setUploadedImageBase64(null);
                setUploadedImageMimeType('image/jpeg');
              }}
              className="ml-auto rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {errDisplay && !isLoading && (
          <div className="space-y-2 p-4">
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errDisplay.message}</span>
            </div>
            <div className="flex gap-2 pl-6">
              {errDisplay.retry && (
                <FosButton variant="outline" size="sm" onClick={errDisplay.retry} className="gap-1">
                  <RefreshCw className="h-3 w-3" />
                  Retry
                </FosButton>
              )}
              {errDisplay.upgrade && (
                <FosButton size="sm" className="gap-1">
                  <Sparkles className="h-3 w-3" />
                  Upgrade to Pro
                </FosButton>
              )}
            </div>
          </div>
        )}

        {result && !isLoading && (
          <div className="p-3">
            {commandMode === 'draft' && draftResult ? (
              <DraftCoPilotCard
                draft={draftResult}
                onDismiss={() => {
                  setResult(null);
                  setDraftResult(null);
                }}
              />
            ) : commandMode === 'identify' && identifyResult ? (
              <IdentifyResultCard
                result={identifyResult}
                onDismiss={() => {
                  setResult(null);
                  setIdentifyResult(null);
                }}
              />
            ) : commandMode === 'appraise' && appraiseResult ? (
              <AppraiseResultCard
                result={appraiseResult}
                onDismiss={() => {
                  setResult(null);
                  setAppraiseResult(null);
                }}
              />
            ) : (
              <ResultCard result={result} />
            )}
          </div>
        )}

        {!query && !isLoading && !result && !errDisplay && (
          <CommandEmpty>
            <div className="space-y-1 py-4 text-sm text-muted-foreground">
              <p className="text-xs font-medium uppercase tracking-wide text-foreground/50">
                Commands
              </p>
              <p>
                <span className="rounded bg-muted px-1 font-mono text-xs">/identify</span> Describe
                this item
              </p>
              <p>
                <span className="rounded bg-muted px-1 font-mono text-xs">/draft</span> Victorian
                oak dresser
              </p>
              <p>
                <span className="rounded bg-muted px-1 font-mono text-xs">/appraise</span> Tiffany
                lamp 1920s
              </p>
              <p className="mt-2 text-xs font-medium uppercase tracking-wide text-foreground/50">
                Or ask naturally
              </p>
              <p>"Show items with margin under 30%"</p>
              <p>"What sold best last month?"</p>
            </div>
          </CommandEmpty>
        )}
      </CommandList>

      {isLimitReached && (
        <div className="flex items-center justify-between border-t bg-muted/30 px-4 py-3 text-sm">
          <span className="text-muted-foreground">Daily free limit reached</span>
          <FosButton size="sm">Upgrade to Pro</FosButton>
        </div>
      )}
    </CommandDialog>
  );
}

export function DraftCoPilotCard({
  draft,
  onDismiss,
}: {
  draft: GenerateListingResult;
  onDismiss: () => void;
}) {
  const [title, setTitle] = useState(draft.title);
  const [description, setDescription] = useState(draft.description);
  const [copied, setCopied] = useState<'title' | 'description' | null>(null);

  async function copyToClipboard(text: string, field: 'title' | 'description') {
    await navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 1500);
  }

  return (
    <Card className="border-primary/20">
      <CardContent className="space-y-3 pt-4">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Title
              <AiTooltip operation="generate" />
            </span>
            <FosButton
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-2 text-xs"
              onClick={() => copyToClipboard(title, 'title')}
            >
              {copied === 'title' ? (
                <Check className="h-3 w-3 text-success-foreground" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
              {copied === 'title' ? 'Copied!' : 'Copy'}
            </FosButton>
          </div>
          <input
            className="w-full border-0 border-b border-border/50 bg-transparent pb-1 text-sm font-medium focus:border-primary focus:outline-none"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Description
            </span>
            <FosButton
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-2 text-xs"
              onClick={() => copyToClipboard(description, 'description')}
            >
              {copied === 'description' ? (
                <Check className="h-3 w-3 text-success-foreground" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
              {copied === 'description' ? 'Copied!' : 'Copy'}
            </FosButton>
          </div>
          <Textarea
            className="min-h-[80px] resize-none bg-transparent text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {draft.conditionStatement && (
          <p className="border-l-2 border-border pl-2 text-xs text-muted-foreground">
            {draft.conditionStatement}
          </p>
        )}

        <div className="flex justify-end">
          <FosButton variant="outline" size="sm" onClick={onDismiss} className="gap-1">
            <X className="h-3 w-3" />
            Dismiss
          </FosButton>
        </div>
      </CardContent>
    </Card>
  );
}

function IdentifyResultCard({
  result,
  onDismiss,
}: {
  result: IdentifyItemResult;
  onDismiss: () => void;
}) {
  return (
    <Card className="border-primary/20">
      <CardContent className="space-y-3 pt-4">
        <div>
          <p className="flex items-center gap-1 text-sm font-medium">
            {result.name}
            <AiTooltip operation="identify" />
          </p>
          <p className="text-xs text-muted-foreground">
            {result.category} · {result.era}
          </p>
        </div>

        <div className="text-sm">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Est. Value
          </span>
          <p className="font-medium">{result.estimatedValue}</p>
        </div>

        {(result.materials ?? []).length > 0 && (
          <div className="text-sm">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Materials
            </span>
            <p>{result.materials.join(', ')}</p>
          </div>
        )}

        <div className="text-sm">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Condition
          </span>
          <p>{result.condition}</p>
        </div>

        {(result.highlights ?? []).length > 0 && (
          <div className="space-y-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Highlights
            </span>
            <ul className="list-inside list-disc text-sm text-muted-foreground">
              {result.highlights.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end">
          <FosButton variant="outline" size="sm" onClick={onDismiss} className="gap-1">
            <X className="h-3 w-3" />
            Dismiss
          </FosButton>
        </div>
      </CardContent>
    </Card>
  );
}

function AppraiseResultCard({
  result,
  onDismiss,
}: {
  result: AppraiseResult;
  onDismiss: () => void;
}) {
  return (
    <Card className="border-primary/20">
      <CardContent className="space-y-3 pt-4">
        <div>
          <p className="flex items-center gap-1 text-sm font-medium">
            {result.item_name}
            <AiTooltip operation="appraise" />
          </p>
          <p className="text-xs text-muted-foreground">{result.category}</p>
        </div>

        <div className="text-sm">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Est. Value
          </span>
          <p className="font-medium">
            ${result.estimated_value.low} – ${result.estimated_value.high}
          </p>
        </div>

        {(result.key_value_drivers ?? []).length > 0 && (
          <div className="space-y-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Value Drivers
            </span>
            <ul className="list-inside list-disc text-sm text-muted-foreground">
              {result.key_value_drivers.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-sm">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Strategy
          </span>
          <p className="text-sm text-muted-foreground">{result.suggested_listing_strategy}</p>
        </div>

        <div className="flex justify-end">
          <FosButton variant="outline" size="sm" onClick={onDismiss} className="gap-1">
            <X className="h-3 w-3" />
            Dismiss
          </FosButton>
        </div>
      </CardContent>
    </Card>
  );
}

function ResultCard({ result }: { result: NaturalLanguageQueryResult }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <Card className="border-primary/20">
      <CardContent className="space-y-3 pt-4">
        <p className="flex items-start gap-1 text-sm">
          <span>{result.summary}</span>
          <AiTooltip operation="nlq" className="mt-0.5 shrink-0" />
        </p>

        {result.items && result.items.length > 0 && (
          <div className="divide-y divide-border rounded-md border">
            {result.items.map((item, i) => (
              <div key={i} className="flex justify-between px-3 py-2 text-sm">
                <span className="text-muted-foreground">{item.label}</span>
                <span className="font-medium">{item.value}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <FosButton
            variant="outline"
            size="sm"
            onClick={() => setDismissed(true)}
            className="gap-1"
          >
            <X className="h-3 w-3" />
            Dismiss
          </FosButton>
          {result.actions?.map((a, i) => (
            <FosButton key={i} size="sm" className="gap-1">
              <Check className="h-3 w-3" />
              {a.label}
            </FosButton>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
